import { useQuery } from '@apollo/client'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import Chart from 'pared/charts'
import SearchableSelect from 'pared/components/basicUi/select/SearchableSelect'
import Spin from 'pared/components/basicUi/spin'
import { MOBILE_WIDTH } from 'pared/constants/styles'
import {
  GROUP_BY_PERIOD,
  GROUP_BY_QUARTER,
  GROUP_BY_YEAR,
  TYPE_PERIOD,
  TYPE_QUARTER,
  TYPE_YEAR,
} from 'pared/data/getDateRanges'
import useGetDateRange from 'pared/hooks/useGetDateRange'

import ChartContainer from './ChartContainer'
import SubHeader from './SubHeader'
import {
  IGetProductHistoricalPerformanceListMenuItemsNodesType,
  IGetProductHistoricalPerformanceType,
  IGetProductHistoricalPerformanceVariablesType,
  getProductHistoricalPerformance,
} from './gqls/historicalPerformance'
import useHistoricalPerformanceConfig from './hooks/useHistoricalPerformanceConfig'

interface IPropsType {
  dateRange: ReturnType<typeof useGetDateRange>
  locationId: number
}

const MenuItemSelect = styled(SearchableSelect)`
  @media ${MOBILE_WIDTH} {
    width: 100%;
  }
` as typeof SearchableSelect

const HistoricalPerformance = ({ dateRange, locationId }: IPropsType) => {
  const [item, setItem] = useState<string[]>([])
  const prevItemRef = useRef(item)
  const { data, previousData, loading } = useQuery<
    IGetProductHistoricalPerformanceType,
    IGetProductHistoricalPerformanceVariablesType
  >(getProductHistoricalPerformance, {
    variables: {
      iStartDate: dateRange?.startDateStr || '',
      iEndDate: dateRange?.endDateStr || '',
      iGroupBy: (() => {
        const groupByMap = {
          [TYPE_YEAR]: GROUP_BY_YEAR,
          [TYPE_QUARTER]: GROUP_BY_QUARTER,
          [TYPE_PERIOD]: GROUP_BY_PERIOD,
        } as const
        const dateType = dateRange?.type || TYPE_PERIOD
        return (
          groupByMap[dateType as keyof typeof groupByMap] || GROUP_BY_PERIOD
        )
      })(),
      iFilter: {
        location_ids: [locationId],
        metrics: ['check_count'],
      },
      iItemCategory: {
        item: item?.[1] || null,
      },
      notSelected: !item?.[1],
    },
    skip: !dateRange,
  })
  const listMenuItemCosting =
    (data || previousData)?.listMenuItemCosting || null
  const listMenuItems = (data || previousData)?.listMenuItems || null
  const historicalPerformanceConfig = useHistoricalPerformanceConfig(
    (!data ? prevItemRef.current : item).join(' / '),
    listMenuItemCosting?.nodes || [],
    (() => {
      const dateType = dateRange?.type || TYPE_PERIOD
      if (dateType === TYPE_YEAR) return TYPE_YEAR
      if (dateType === TYPE_QUARTER) return TYPE_QUARTER
      return TYPE_PERIOD
    })(),
  )
  const dataSource = useMemo(
    () => [
      { id: 'root', parentId: null, value: 'root', text: 'root' },
      ...(listMenuItems?.nodes || []),
    ],
    [listMenuItems],
  )

  useEffect(() => {
    if (data) prevItemRef.current = item
  }, [data, item])

  if (!listMenuItems)
    // TODO: should have loading page
    return <div>loading</div>

  return (
    <>
      <SubHeader>
        <h1>Historical Performance</h1>

        <div>
          <h3>Menu Item:</h3>

          <MenuItemSelect<IGetProductHistoricalPerformanceListMenuItemsNodesType>
            value={item}
            onChange={setItem}
            dataSource={dataSource}
            searchable={true}
          />
        </div>
      </SubHeader>

      {!listMenuItemCosting ? (
        // TODO: should have loading page
        <div>loading</div>
      ) : (
        <ChartContainer>
          <Spin spinning={loading}>
            <Chart {...historicalPerformanceConfig} width={1100} height={650} />
          </Spin>
        </ChartContainer>
      )}
    </>
  )
}

export default React.memo(
  HistoricalPerformance,
) as unknown as typeof HistoricalPerformance
